import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastContainer } from './components/Toast';
import GlobalAlertModal from './components/GlobalAlertModal';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import VerifyEmail from './pages/VerifyEmail';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import EnquiryList from './pages/EnquiryList';
import EnquiryDetail from './pages/EnquiryDetail';
import ProjectList from './pages/ProjectList';
import ProjectDetail from './pages/ProjectDetail';
import CustomerList from './pages/CustomerList';
import CustomerDetail from './pages/CustomerDetail';
import VendorList from './pages/VendorList';
import VendorDetail from './pages/VendorDetail';
import UserList from './pages/UserList';
import CalendarPage from './pages/CalendarPage';
import SupervisorUpdatePage from './pages/SupervisorUpdatePage';
import MySupervisorUpdates from './pages/MySupervisorUpdates';
import SupervisorUpdateApprovals from './pages/SupervisorUpdateApprovals';
import EmployeeList from './pages/EmployeeList';
import EmployeeDetail from './pages/EmployeeDetail';
import PayrollPage from './pages/PayrollPage';
import ReportsPage from './pages/ReportsPage';
import AuditLogPage from './pages/AuditLogPage';
import TimesheetPage from './pages/TimesheetPage';
import MyTimesheets from './pages/MyTimesheets';
import TimesheetApprovals from './pages/TimesheetApprovals';
import RoleManagement from './pages/RoleManagement';
import MenuManagement from './pages/MenuManagement';
import DashboardRouter from './components/DashboardRouter';
import Layout from './components/Layout';
import SuperAdminDashboard from './pages/superadmin/SuperAdminDashboard';
import CompanyManagement from './pages/superadmin/CompanyManagement';
import PlanManagement from './pages/superadmin/PlanManagement';
import BillingOverview from './pages/superadmin/BillingOverview';
import WhatsAppConfig from './pages/superadmin/WhatsAppConfig';
import BranchManagement from './pages/company/BranchManagement';
import SubscriptionManagement from './pages/company/SubscriptionManagement';
import TrialExpired from './pages/TrialExpired';
import ChangePassword from './pages/ChangePassword';
import AccountHeads from './pages/AccountHeads';
import ProjectTypes from './pages/ProjectTypes';
import ProjectConsumption from './pages/ProjectConsumption';
import FinanceDashboard from './pages/FinanceDashboard';
import FinancialTransactions from './pages/FinancialTransactions';
import CustomerPayments from './pages/CustomerPayments';
import VendorPurchases from './pages/VendorPurchases';
import VendorPaymentsPage from './pages/VendorPaymentsPage';
import CashFlow from './pages/CashFlow';
import CashAdvance from './pages/CashAdvance';
import FinanceReports from './pages/FinanceReports';
import FinanceApprovalSettings from './pages/FinanceApprovalSettings';
import FinanceApprovalsPage from './pages/FinanceApprovalsPage';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import CompanySettings from './pages/CompanySettings';
import WhatsAppSettings from './pages/WhatsAppSettings';
import ThemeCustomization from './pages/ThemeCustomization';
import InventoryList from './pages/InventoryList';
import InventoryCategories from './pages/InventoryCategories';
import InventoryDetail from './pages/InventoryDetail';
import WarehouseList from './pages/WarehouseList';
import StockTransferList from './pages/StockTransferList';

function App() {
  return (
    <Router>
      <AuthProvider>
        <ThemeProvider>
          <ToastContainer />
          <GlobalAlertModal />
          <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/trial-expired" element={<TrialExpired />} />
          <Route path="/change-password" element={
            <ProtectedRoute>
              <ChangePassword />
            </ProtectedRoute>
          } />
          <Route path="/onboarding" element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          } />

          {/* Super Admin routes */}
          <Route element={<ProtectedRoute requireRole="SUPER_ADMIN" />}>
            <Route path="/superadmin" element={<Layout />}>
              <Route index element={<SuperAdminDashboard />} />
              <Route path="companies" element={<CompanyManagement />} />
              <Route path="plans" element={<PlanManagement />} />
              <Route path="billing" element={<BillingOverview />} />
              <Route path="users" element={<UserList />} />
              {/* Roles & Permissions removed from Super Admin — Requirement 12.2, 12.3 */}
              <Route path="roles" element={<Navigate to="/superadmin" replace />} />
              <Route path="permissions" element={<Navigate to="/superadmin" replace />} />
              <Route path="menus" element={<MenuManagement />} />
              <Route path="whatsapp-config" element={<WhatsAppConfig />} />
            </Route>
          </Route>

          {/* Main app routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<Layout />}>
            <Route index element={<DashboardRouter />} />
            <Route path="profile" element={<Profile />} />
            <Route path="enquiries" element={<EnquiryList />} />
            <Route path="enquiries/:id" element={<EnquiryDetail />} />
            <Route path="projects" element={<ProjectList />} />
            <Route path="projects/consumption" element={<ProjectConsumption />} />
            <Route path="projects/:id" element={<ProjectDetail />} />
            <Route path="project-types" element={<ProjectTypes />} />
            <Route path="customers" element={<CustomerList />} />
            <Route path="customers/:id" element={<CustomerDetail />} />
            <Route path="vendors" element={<VendorList />} />
            <Route path="vendors/:id" element={<VendorDetail />} />
            <Route path="employees" element={<EmployeeList />} />
            <Route path="employees/:id" element={<EmployeeDetail />} />
            <Route path="payroll" element={<PayrollPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="users" element={<UserList />} />
            <Route path="roles" element={<RoleManagement />} />
            <Route path="menus" element={
              <ProtectedRoute requireRole="COMPANY_ADMIN" requireMenu="menus">
                <MenuManagement />
              </ProtectedRoute>
            } />
            <Route path="timesheets" element={<TimesheetPage />} />
            <Route path="timesheets/my" element={<MyTimesheets />} />
            <Route path="timesheets/approvals" element={<TimesheetApprovals />} />
            <Route path="my-timesheets" element={<Navigate to="/timesheets/my" replace />} />
            <Route path="audit-logs" element={
              <ProtectedRoute requireRole="COMPANY_ADMIN" requireMenu="audit-logs">
                <AuditLogPage />
              </ProtectedRoute>
            } />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="supervisor-updates/new" element={<SupervisorUpdatePage />} />
            <Route path="supervisor-updates/edit/:id" element={<SupervisorUpdatePage />} />
            <Route path="supervisor-updates/my" element={<MySupervisorUpdates />} />
            <Route path="supervisor-updates/approvals" element={<SupervisorUpdateApprovals />} />
            <Route path="branches" element={
              <ProtectedRoute requireRole="COMPANY_ADMIN" requireMenu="branches">
                <BranchManagement />
              </ProtectedRoute>
            } />
            <Route path="subscription" element={
              <ProtectedRoute requireRole="COMPANY_ADMIN" requireMenu="subscription">
                <SubscriptionManagement />
              </ProtectedRoute>
            } />
            <Route path="settings" element={
              <ProtectedRoute requireRole="COMPANY_ADMIN" requireMenu="company-settings">
                <Navigate to="/settings/company" replace />
              </ProtectedRoute>
            } />
            <Route path="settings/company" element={
              <ProtectedRoute requireRole="COMPANY_ADMIN" requireMenu="company-settings">
                <CompanySettings />
              </ProtectedRoute>
            } />
            <Route path="settings/email" element={
              <ProtectedRoute requireRole="COMPANY_ADMIN" requireMenu="email-settings">
                <Settings />
              </ProtectedRoute>
            } />
            <Route path="settings/whatsapp" element={
              <ProtectedRoute requireRole="COMPANY_ADMIN" requireMenu="whatsapp-settings">
                <WhatsAppSettings />
              </ProtectedRoute>
            } />
            <Route path="settings/theme" element={
              <ProtectedRoute requireRole="COMPANY_ADMIN" requireMenu="theme-settings">
                <ThemeCustomization />
              </ProtectedRoute>
            } />
            
            {/* Financial Management Routes */}
            <Route path="finance" element={<FinanceDashboard />} />
            <Route path="finance/account-heads" element={<AccountHeads />} />
            <Route path="finance/transactions" element={<FinancialTransactions />} />
            <Route path="finance/customer-payments" element={<CustomerPayments />} />
            <Route path="finance/vendor-purchases" element={<VendorPurchases />} />
            <Route path="finance/purchase-returns" element={<VendorPurchases isReturn={true} />} />
            <Route path="finance/vendor-payments" element={<VendorPaymentsPage />} />
            <Route path="finance/cash-bank" element={<CashFlow />} />
            <Route path="finance/cash-advance" element={<CashAdvance />} />
            <Route path="finance/reports" element={<FinanceReports />} />
            <Route path="finance/approvals" element={<FinanceApprovalsPage />} />
            <Route path="finance/approval-settings" element={
              <ProtectedRoute requireRole="COMPANY_ADMIN" requireMenu="finance-approval-settings">
                <FinanceApprovalSettings />
              </ProtectedRoute>
            } />
            
            {/* Inventory Management Routes */}
            <Route path="materials" element={<InventoryList defaultType="MATERIAL" />} />
            <Route path="assets" element={<InventoryList defaultType="ASSET" />} />
            <Route path="inventory" element={<InventoryList defaultType="MATERIAL" />} />
            <Route path="inventory/categories" element={<InventoryCategories />} />
            <Route path="inventory/warehouses" element={<WarehouseList />} />
            <Route path="inventory/transfers" element={<StockTransferList />} />
            <Route path="inventory/:id" element={<InventoryDetail />} />
            </Route>
          </Route>
        </Routes>
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
